import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useForgotPassword } from '@/lib/auth';

import { AuthCard, FormField } from './LoginPage';

export function ForgotPasswordPage() {
  const forgot = useForgotPassword();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await forgot.mutateAsync(email.trim());
    } catch {
      /* even on error we show the same message — backend always returns 202 */
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <AuthCard
        title="Check your email"
        subtitle="If an account exists for that address, a reset link is on its way."
      >
        <p className="text-sm text-stone-600 dark:text-stone-400">
          The link expires in about an hour. If your instance doesn't have SMTP
          configured, the reset link is logged to the server console — ask your
          admin to fish it out of the container logs.
        </p>
        <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
          <Link to="/login" className="text-forge-700 dark:text-forge-400 hover:underline">
            Back to sign-in
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset your password"
      subtitle="Enter the email you signed up with and we'll send a reset link."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Email">
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <Button type="submit" className="w-full" disabled={forgot.isPending}>
          {forgot.isPending ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
        Remembered it?{' '}
        <Link to="/login" className="text-forge-700 dark:text-forge-400 hover:underline">
          Sign in
        </Link>
      </p>
    </AuthCard>
  );
}
